import { apiRequest, apiRequestPaginated } from './client';
import type { ServiceCategory, Subcategory } from '../types';

type ApiService = {
  _id: string;
  name: string;
  description?: string;
  createdAt?: string;
};

type ApiCategory = {
  _id: string;
  name: string;
  service?: { _id: string; name: string } | string | null;
  createdAt?: string;
};

type ApiSubcategory = {
  _id: string;
  name: string;
  category?: { _id: string; name: string } | null;
  service?: { _id: string; name: string } | null;
  createdAt?: string;
};

const serviceIdOf = (service?: ApiCategory['service']) => {
  if (!service) return '';
  if (typeof service === 'string') return service;
  return service._id || '';
};

export const mapCategory = (c: ApiCategory, subcategories: Subcategory[] = []): ServiceCategory => ({
  id: c._id,
  name: c.name,
  description: '',
  icon: (c.name || 'C').slice(0, 2).toUpperCase(),
  status: 'Active',
  createdAt: c.createdAt || '',
  providerCount: 0,
  requestCount: 0,
  activeJobs: 0,
  completedJobs: 0,
  subcategories,
  serviceId: serviceIdOf(c.service),
});

export const mapSubcategory = (s: ApiSubcategory): Subcategory => ({
  id: s._id,
  name: s.name,
  description: '',
  status: 'Active',
  createdAt: s.createdAt || '',
});

export const servicesApi = {
  getAllServices: async (): Promise<ApiService[]> => {
    const data = await apiRequest<{ services: ApiService[] }>('/admin/service/get-all-services', {
      method: 'GET',
      auth: true,
    });
    return data.services || [];
  },

  getAllCategories: async (): Promise<ServiceCategory[]> => {
    const data = await apiRequest<{ categories: ApiCategory[] }>(
      '/admin/provider/get-all-categories',
      { method: 'GET', auth: true }
    );
    return (data.categories || []).map((c) => mapCategory(c));
  },

  getAllSubcategories: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    service?: string;
  }): Promise<{ subcategories: ApiSubcategory[]; total: number }> => {
    const query = new URLSearchParams({
      page: String(params?.page || 1),
      limit: String(params?.limit || 1000),
    });
    if (params?.search?.trim()) query.set('search', params.search.trim());
    if (params?.service) query.set('service', params.service);

    const { data, pagination } = await apiRequestPaginated<{ subcategories: ApiSubcategory[] }>(
      `/admin/service/get-all-subcategories?${query.toString()}`,
      { method: 'GET', auth: true }
    );

    return {
      subcategories: data.subcategories || [],
      total: pagination.totalItems || 0,
    };
  },

  getCategoryById: async (id: string): Promise<ServiceCategory> => {
    const categories = await servicesApi.getAllCategories();
    const category = categories.find((c) => c.id === id);
    if (!category) throw new Error('Category not found');

    const { subcategories } = await servicesApi.getAllSubcategories({
      page: 1,
      limit: 1000,
      service: category.serviceId,
    });

    const filtered = subcategories
      .filter((s) => s.category?._id === id)
      .map(mapSubcategory);

    return { ...category, subcategories: filtered };
  },

  createSubcategory: async (payload: {
    serviceID: string;
    categoryID: string;
    name: string;
  }): Promise<void> => {
    await apiRequest('/admin/service/create-subcategory', {
      method: 'POST',
      auth: true,
      body: payload,
    });
  },

  editSubcategory: async (subcategoryID: string, newName: string): Promise<void> => {
    await apiRequest('/admin/service/edit-subcategory', {
      method: 'POST',
      auth: true,
      body: { subcategoryID, newName },
    });
  },
};
